import { Button, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import FilterList from 'components/FilterList';
import Modal, { type ModalOrigin } from 'components/Modal';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { ClickOutsideComponent, ClickOutsideProps } from 'hooks/useOnClickOutside';
import { withOnClickOutside } from 'hooks/useOnClickOutside';
import memoizeOne from 'memoize-one';
import React, { Component } from 'react';
import type { TagListOption } from 'types/tag';
import type { OptionMeta } from './FilterListItem';
import { InfoIcon } from './Icons';
import styles from './TagListDropdown.module.scss';

interface Props extends ClickOutsideProps<HTMLDivElement> {
  lists: Array<TagListOption>;
  selectedLists: Array<number>;
  label?: string;
  buttonClassName: string;
  className: string;
  manageLinkTo: string;
  onChange: Function;
  tagListAddBatch: () => void;
  tagListRemoveBatch: () => void;
  isDefault?: boolean;
  otherSelectedTagLists?: Array<number>;
  'data-testid'?: string;
  modalOrigin?: ModalOrigin;
}

interface State {
  isOpen: boolean;
}

class TagListDropdown extends Component<Props, State> implements ClickOutsideComponent {
  static defaultProps = {
    lists: [],
    label: 'Add existing tag list',
    modalOrigin: 'bottomLeftToSouth' as ModalOrigin,
  };

  state: State = {
    isOpen: false,
  };

  handleClickOutside = (): void => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
    }
  };

  handleTriggerClick = (): void => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleModalKeyPress = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation(); // don't bubble
      this.handleClickOutside();
    }
  };

  handleOnChange = (list: TagListOption, listIds: Array<number>): void => {
    this.props.onChange(list.value, listIds);
  };

  handleBatchSelect = (batch: string) => {
    if (batch === 'all') {
      this.props.tagListAddBatch();
    }

    if (batch === 'none') {
      this.props.tagListRemoveBatch();
    }
  };

  getExtraMeta = memoizeOne(
    (ids: Array<number> = EMPTY_ARRAY, _options: Array<TagListOption> = EMPTY_ARRAY) => {
      const specifier = this.props.isDefault ? 'selectable by users' : 'set by default';
      const reverseSpecifier = this.props.isDefault ? 'set by default' : 'selectable by users';

      const extraMeta = ids.reduce(
        (acc, id) => {
          acc[id] = {
            hint: this.props.isDefault ? 'User-selectable tag list' : 'Default tag list',
            hintTooltip: (
              <Tooltip
                fixed
                tooltip={`This tag list is currently ${specifier}. By making this tag list ${reverseSpecifier}, it will be moved here instead.`}
              >
                <InfoIcon />
              </Tooltip>
            ),
          };
          return acc;
        },
        {} as Record<number, Partial<OptionMeta>>
      );

      return extraMeta;
    }
  );

  render() {
    return (
      <div
        ref={this.props.clickOutsideRef}
        className={classNames(styles.container, this.props.className)}
      >
        <Button
          data-testid={this.props['data-testid'] || 'add_existing_tag_list_dropdown'}
          priority="low"
          onClick={this.handleTriggerClick}
          caret
          isActive={this.state.isOpen}
          className={this.props.buttonClassName}
        >
          {this.props.label}
        </Button>
        <Modal
          onKey={this.handleModalKeyPress}
          isOpen={this.state.isOpen}
          origin={this.props.modalOrigin}
          defaultWidth={400}
          defaultHeight={220}
          className={styles.filterList}
        >
          <div className={styles.filterListHeader}>
            <span>Tag lists</span>
          </div>
          <div className={styles.filterListContent}>
            <FilterList
              options={this.props.lists}
              values={this.props.selectedLists}
              // @ts-expect-error
              onChange={this.handleOnChange}
              onBatchSelect={this.handleBatchSelect}
              extraMeta={this.getExtraMeta(this.props.otherSelectedTagLists, this.props.lists)}
              hideBatchOptions={this.props.isDefault}
            />
          </div>
        </Modal>
      </div>
    );
  }
}

export default withOnClickOutside(TagListDropdown);
